import { useCallback, useMemo } from 'react';

import type { RendererProps } from '@atlaskit/renderer/renderer-props';

import { expValEquals } from '@confluence/feature-experiments';
import { shouldDisplayExtensionAsInline } from '@confluence/fabric-extension-lib/entry-points/shouldDisplayExtensionAsInline.ts';

import { readMacroPresetConfig } from './readMacroPresetConfig.ts';

type ExtensionNode = Parameters<NonNullable<RendererProps['getExtensionHeight']>>[0];
type MacroHeightPresetOptions = Pick<RendererProps, 'scheduleExtensionHeightRemoval'> & {
	getPresetMinHeight?: (extension: ExtensionNode | string | undefined) => string | undefined;
	getPresetHeight?: (extension: ExtensionNode | string | undefined) => number | undefined;
};

const isBlockMacro = (node: ExtensionNode): boolean =>
	!node.isInline &&
	!shouldDisplayExtensionAsInline({
		type: node.type.name === 'bodiedExtension' ? 'bodiedExtension' : 'extension',
		extensionKey: node.attrs.extensionKey,
		extensionType: node.attrs.extensionType,
		parameters: node.attrs.parameters,
	});
const getHeightForExtensionKey = (
	heights: Record<string, number> | undefined,
	extensionKey?: string,
): number | undefined =>
	heights && extensionKey && Object.prototype.hasOwnProperty.call(heights, extensionKey)
		? heights[extensionKey]
		: undefined;

export const useMacroHeightPreset = (enabled = true): MacroHeightPresetOptions => {
	const presets = useMemo(() => {
		if (enabled && expValEquals('confluence_macro_preset_temp_height', 'isEnabled', true)) {
			return readMacroPresetConfig();
		}
		return undefined;
	}, [enabled]);
	const getPresetMinHeight = useCallback<
		NonNullable<MacroHeightPresetOptions['getPresetMinHeight']>
	>(
		(extension) => {
			const node = typeof extension === 'string' ? undefined : extension;
			const extensionKey = typeof extension === 'string' ? extension : node?.attrs.extensionKey;
			const height = getHeightForExtensionKey(presets?.minHeight, extensionKey);
			if (height === undefined || (node && !isBlockMacro(node))) return undefined;
			return String(height);
		},
		[presets],
	);
	const getPresetHeight = useCallback<NonNullable<MacroHeightPresetOptions['getPresetHeight']>>(
		(extension) => {
			const node = typeof extension === 'string' ? undefined : extension;
			const extensionKey = typeof extension === 'string' ? extension : node?.attrs.extensionKey;
			const height = getHeightForExtensionKey(presets?.presetHeight, extensionKey);
			if (height === undefined || (node && !isBlockMacro(node))) return undefined;
			return height;
		},
		[presets],
	);
	const scheduleExtensionHeightRemoval = useCallback<
		NonNullable<RendererProps['scheduleExtensionHeightRemoval']>
	>(
		(node, remove, property) => {
			const heights = property === 'minHeight' ? presets?.minHeight : presets?.presetHeight;
			if (
				getHeightForExtensionKey(heights, node.attrs.extensionKey) === undefined ||
				!isBlockMacro(node)
			)
				return;
			const delay =
				property === 'minHeight' ? presets?.minHeightRemoval : presets?.presetHeightRemoval;
			const timer = setTimeout(remove, delay);
			return () => clearTimeout(timer);
		},
		[presets],
	);
	return {
		getPresetMinHeight: presets ? getPresetMinHeight : undefined,
		getPresetHeight: presets ? getPresetHeight : undefined,
		scheduleExtensionHeightRemoval: presets ? scheduleExtensionHeightRemoval : undefined,
	};
};
